import { Injectable } from '@angular/core';
import { RestService } from './rest.service';
import { AuthService } from './auth.service';
import { tap } from 'rxjs';
import { LoginResponse } from '../models/dto/login-response';
import { LoginRequest } from '../models/dto/login-request';

/**
 * LoginService
 */
@Injectable({
  providedIn: 'root',
})
export class LoginService {
  /**
   * constructor
   *
   * @param _restService
   */
  constructor(
    private _restService: RestService,
    private _authService: AuthService
  ) {}

  /**
   * send login request to the backend
   *
   * The backend returns one of three shapes, all with success=true:
   *  - a full session (token/role/user) -> store it,
   *  - mfa_required (+pre_auth_token)    -> do NOT store, caller routes to verify,
   *  - mfa_setup_required (+setup_token) -> do NOT store, caller routes to setup.
   * We must branch BEFORE storing, otherwise an MFA response (empty token) would
   * be persisted as a broken session.
   *
   * @param request
   * @returns the raw LoginResponse
   */
  postLogin(request: LoginRequest) {
    return this._restService.post<LoginResponse>('/auth/login', request).pipe(
      tap((response: LoginResponse) => {
        if (response.mfa_setup_required || response.mfa_required) {
          // Two-step login: nothing to store yet; the component routes onward.
          return;
        }
        if (response.success) {
          this.applyLoginResponse(response);
        }
      })
    );
  }

  /**
   * Persist a completed login (full JWT) into AuthService. Shared by the
   * password-login path and the MFA verify path.
   */
  applyLoginResponse(response: LoginResponse) {
    this._authService.reset();
    this._authService.token = response.token ?? '';
    this._authService.company = response.company!;
    this._authService.role = response.role ?? 'Guest';
    this._authService.user = response.user!;
  }
}
